/**
 * Planning Engine Overview — summary cards and engine flow diagram.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Zap,
  CheckCircle2,
  Clock,
  AlertTriangle,
  XCircle,
  HelpCircle,
  Ruler,
  ArrowRight,
  Loader2,
  Rocket,
  Target,
} from 'lucide-react';
import { usePlanningEngine } from './PlanningEngineContext';
import { CreateScenarioModal } from './ScenarioList';
import { engineInputKindLabel } from '../../../lib/planningEngineService';
import { PLANNING_MODE_LABELS } from '../../../lib/planningEngineModule.types';
import { useTimePhasedPlanningEnabled } from '../../../features/time-phased-planning/hooks/useTimePhasedPlanningEnabled';
import { PlanningEngineSetupChecklist } from './planningEngineChecklist';
import ScenarioSummarySentence from './ScenarioSummarySentence';
import NonPlannableScopeNotice, { useScopeIsNotPlannable } from './NonPlannableScopeNotice';
import ParticipationInbox from './ParticipationInbox';
import { COMMIT_TO_PLAN_VERSION_LABEL, REVIEW_AND_COMMIT_LABEL } from '../../../lib/planning/vocabulary';

// ─── Active scenario card (read-only summary) ──────────────────────────────
//
// Switching the active scenario lives in exactly one place: the Scenario
// picker in the tab-bar scope bar (EngineScenarioPicker). This card used to
// carry its own separate, unstyled native <select> switcher — a duplicate
// control with none of the tab-bar picker's cycle grouping or dedupe
// labeling. Removed; this is now a plain summary with a link to manage.

function ActiveScenarioCard() {
  const { scenarios, activeScenario } = usePlanningEngine();

  return (
    <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] p-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">
          Active Scenario
        </h2>
        {scenarios.length > 0 && (
          <Link
            to="/planning/engine/scenarios"
            className="text-xs text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
          >
            Manage
          </Link>
        )}
      </div>

      {activeScenario ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">Planning Weeks</div>
            <div className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {activeScenario.planningWeeks}
            </div>
          </div>
          <div>
            <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">Capacity Buffer</div>
            <div className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {Math.round(activeScenario.capacityBufferPercent * 100)}%
            </div>
          </div>
          <div>
            <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">WU Hours</div>
            <div className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {activeScenario.defaultWorkUnitHours}h
            </div>
          </div>
          <div>
            <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">Mode</div>
            <div className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {PLANNING_MODE_LABELS[activeScenario.planningMode] ?? activeScenario.planningMode}
            </div>
          </div>
          <div>
            <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">Include Not Ready</div>
            <div className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {activeScenario.includeNotReady ? 'Yes' : 'No'}
            </div>
          </div>
          <div>
            <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">Include Blocked</div>
            <div className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {activeScenario.includeBlocked ? 'Yes' : 'No'}
            </div>
          </div>
        </div>
      ) : (
        <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
          No active scenario.{' '}
          <Link to="/planning/engine/scenarios" className="text-primary-600 hover:underline">
            Create a scenario
          </Link>{' '}
          to get started.
        </p>
      )}
    </div>
  );
}

// ─── Overview page ────────────────────────────────────────────────────────────

export default function PlanningEngineOverview() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const autoRunTriggered = useRef(false);
  const [showCreateScenario, setShowCreateScenario] = useState(false);
  // BREAK 2: "Open in Planning" → plan created → scenario bound + scope set
  // (CreatePlanModal / planScenarioSetup.ts) lands here with a one-time
  // confirmation in router state, the same idiom as `autoRunLive` below.
  const [setupConfirmation, setSetupConfirmation] = useState<string | null>(null);

  const {
    projectId,
    activeScenario,
    planOutput,
    running,
    error,
    engineInput,
    scopedItemIds,
    activeRunUpdatedAt,
    runEngineForScenario,
    runEngineWithLiveItems,
    runEngineWithSelectedInput,
    refreshScenarios,
    runBlockedReason,
    lastRunNotice,
  } = usePlanningEngine();
  const timePhasedEnabled = useTimePhasedPlanningEnabled();
  const scopeIsNotPlannable = useScopeIsNotPlannable();

  // Scenario list load lives in PlanningEngineProvider — do not re-fetch here.
  // A second refreshScenarios effect raced applyBoundScopeForScenario and reset
  // user-chosen Item Pool scopes back to derived "Scenario priorities".

  useEffect(() => {
    const shouldAutoRun =
      (location.state as { autoRunLive?: boolean } | null)?.autoRunLive === true ||
      searchParams.get('run') === 'live';

    if (!shouldAutoRun || autoRunTriggered.current) return;

    autoRunTriggered.current = true;
    void runEngineWithLiveItems();

    if (searchParams.get('run') === 'live') {
      const next = new URLSearchParams(searchParams);
      next.delete('run');
      setSearchParams(next, { replace: true });
    }

    if ((location.state as { autoRunLive?: boolean } | null)?.autoRunLive) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [
    location.pathname,
    location.state,
    navigate,
    runEngineWithLiveItems,
    searchParams,
    setSearchParams,
  ]);

  // BREAK 2 confirmation — read once, then clear so a refresh/back doesn't
  // re-show it.
  const setupConfirmationCaptured = useRef(false);
  useEffect(() => {
    const confirmation = (location.state as { setupConfirmation?: string } | null)?.setupConfirmation;
    if (!confirmation || setupConfirmationCaptured.current) return;
    setupConfirmationCaptured.current = true;
    setSetupConfirmation(confirmation);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  // Instance output is already scoped at run time — use it directly.
  // Do not fall back to an unscoped summary while a non-live scope is selected.
  const exec = planOutput?.executiveSummary ?? null;
  const scopeResolving =
    engineInput.type !== 'live' && scopedItemIds != null && scopedItemIds.size === 0 && !planOutput;

  const scopeNote = planOutput
    ? engineInput.type !== 'live'
      ? `Instance: ${engineInputKindLabel(engineInput.type)} “${'name' in engineInput ? engineInput.name : ''}” · ${exec?.totalWorkUnits ?? 0} rows${activeRunUpdatedAt ? ` · saved ${new Date(activeRunUpdatedAt).toLocaleString()}` : ''}`
      : `Instance: All Live Items · ${exec?.totalWorkUnits ?? 0} rows${activeRunUpdatedAt ? ` · saved ${new Date(activeRunUpdatedAt).toLocaleString()}` : ''}`
    : engineInput.type !== 'live'
      ? `Scoped to ${(engineInput as { name: string }).name} — run this instance to see totals`
      : null;

  const statCards = [
    {
      label: 'Total Work Units',
      value: exec?.totalWorkUnits ?? '—',
      icon: HelpCircle,
      color: 'text-[var(--text-sec)] dark:text-[var(--text-sec)]',
      bg: 'bg-[var(--surface)] dark:bg-[var(--bg)]',
    },
    {
      label: 'Included',
      value: exec?.includedCount ?? '—',
      icon: CheckCircle2,
      color: 'text-[var(--green)] dark:text-[var(--text-ter)]',
      bg: 'bg-[var(--green-dim)] dark:bg-[var(--green)]/20',
    },
    {
      label: 'Out - capacity',
      value: exec?.deferredCount ?? '—',
      icon: Clock,
      color: 'text-[var(--mustard)] dark:text-[var(--mustard)]',
      bg: 'bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20',
    },
    {
      label: 'Blocked',
      value: exec?.blockedCount ?? '—',
      icon: XCircle,
      color: 'text-[var(--coral)] dark:text-[var(--coral)]',
      bg: 'bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20',
    },
    {
      label: 'Needs Sizing',
      value: exec?.needsSizingCount ?? '—',
      icon: Ruler,
      color: 'text-[var(--text-sec)]',
      bg: 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20',
    },
    {
      label: 'Not Ready',
      value: exec?.notReadyCount ?? '—',
      icon: AlertTriangle,
      color: 'text-[var(--mustard)] dark:text-[var(--mustard)]',
      bg: 'bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20',
    },
    {
      label: 'No Capacity Model',
      value: exec?.noCapacityModelCount ?? '—',
      icon: HelpCircle,
      color: 'text-[var(--text-sec)]',
      bg: 'bg-[var(--surface)] dark:bg-[var(--bg)]/20',
    },
  ];

  return (
    <div className="p-6 w-full space-y-6 overflow-y-auto h-full">
      {/* BREAK 2: one-time confirmation after "Open in Planning" auto-set-up */}
      {setupConfirmation && (
        <div className="flex items-center justify-between gap-3 border border-[var(--green)] bg-[var(--green-dim)] px-4 py-2.5">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-[var(--green)] flex-shrink-0" />
            <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">{setupConfirmation}</p>
          </div>
          <button
            onClick={() => setSetupConfirmation(null)}
            className="text-xs font-medium text-[var(--green)] dark:text-[var(--text-ter)] hover:underline flex-shrink-0"
          >
            Dismiss
          </button>
        </div>
      )}
      {/* Header — wraps on a phone: title and description first, Run below */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-[1_1_20rem]">
          <div className="flex items-center gap-2">
            <Zap className="h-6 w-6 text-primary-600 dark:text-primary-400" />
            <h1 className="text-2xl font-bold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              Planning Engine
            </h1>
          </div>
          <p className="mt-1 text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] max-w-2xl">
            One workbench, two capabilities that share scenario and scope:{' '}
            <span className="font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)]">Viability</span>{' '}
            (rough-cut capacity) and{' '}
            {timePhasedEnabled ? (
              <span className="font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)]">
                Launch planning
              </span>
            ) : (
              'launch planning'
            )}{' '}
            (time-phased execution). Run evaluates the active scope and persists results.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => void runEngineWithSelectedInput()}
            disabled={running || runBlockedReason != null}
            title={runBlockedReason ?? 'Refresh live items, run this Scenario × Scope instance, and save to the database'}
            className="flex items-center gap-2 px-4 py-2 border border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300 bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] hover:bg-primary-50 dark:hover:bg-primary-900/20 disabled:opacity-50 text-sm font-medium transition-colors"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
            Sync &amp; Run
          </button>
          <button
            onClick={() => void runEngineForScenario()}
            disabled={running || runBlockedReason != null}
            title={runBlockedReason ?? 'Run this Scenario × Scope instance and save results (does not wipe other scopes)'}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-sm font-medium transition-colors"
          >
            {running ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Zap className="h-4 w-4" />
            )}
            Run
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)] p-4 text-sm text-[var(--coral)] dark:text-[var(--coral)]">
          {error}
        </div>
      )}

      {!error && !running && runBlockedReason && (
        <p className="text-sm text-[var(--text-sec)]">{runBlockedReason}</p>
      )}

      {lastRunNotice && !running && (
        <div
          role="status"
          className="flex items-center justify-between gap-3 flex-wrap bg-[var(--green-dim)] dark:bg-[var(--green)]/20 border border-[var(--green)] px-3 py-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)]"
        >
          <span>{lastRunNotice}</span>
          <Link to="/planning/engine/summary" className="font-medium underline whitespace-nowrap">
            Next: {COMMIT_TO_PLAN_VERSION_LABEL.toLowerCase()} a plan version
          </Link>
        </div>
      )}

      {/* Active scenario card — with inline scenario switcher */}
      <ActiveScenarioCard />

      {/* The scenario said out loud, before anyone has to reverse-engineer it. */}
      <ScenarioSummarySentence />

      {scopeNote && (
        <div className="bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border border-[var(--slate)] dark:border-[var(--slate)] px-3 py-2 text-sm text-[var(--text-sec)]">
          {scopeNote}
        </div>
      )}

      {/* The receiving side of cross-functional asks. Renders nothing unless
          something is actually addressed to this person. */}
      <ParticipationInbox />

      {/* An attention-shaped scope gets an explanation, not a zero-capacity run. */}
      {scopeIsNotPlannable && <NonPlannableScopeNotice />}

      {/* Run prerequisites — what you need set up IN ORDER to run. Before the
          first run this is the point of the page, so it sits above the empty
          results panel rather than below it as decorated failure. */}
      {!planOutput && !scopeIsNotPlannable && (
        <PlanningEngineSetupChecklist
          activeScenario={activeScenario}
          projectId={projectId}
          planOutput={planOutput}
          executiveSummary={exec}
          onCreateScenario={() => setShowCreateScenario(true)}
          timePhasedEnabled={timePhasedEnabled}
        />
      )}

      {/* Stat cards */}
      {scopeIsNotPlannable ? null : planOutput ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
          {statCards.map((card) => (
            <div
              key={card.label}
              className={`${card.bg}  border border-transparent p-4 flex flex-col gap-1`}
            >
              <card.icon className={`h-5 w-5 ${card.color}`} />
              <div className={`text-2xl font-bold ${card.color}`}>{card.value}</div>
              <div className="text-xs text-[var(--text-sec)]">{card.label}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] p-8 text-center">
          {running || scopeResolving ? (
            <Loader2 className="h-10 w-10 text-primary-500 mx-auto mb-3 animate-spin" />
          ) : (
            <Zap className="h-10 w-10 text-[var(--text-ter)] dark:text-[var(--text-ter)] mx-auto mb-3" />
          )}
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
            {running
              ? 'Running this Scenario × Scope instance…'
              : engineInput.type !== 'live'
                ? `No saved run for this scope yet. Click Run to evaluate “${(engineInput as { name: string }).name}” only.`
                : 'No engine run yet for All Live Items. Click Run to generate results.'}
          </p>
        </div>
      )}

      {/* Two capabilities — shared scenario, different jobs */}
      <div className={`grid gap-4 ${timePhasedEnabled ? 'md:grid-cols-2' : 'grid-cols-1'}`}>
        <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] p-5 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            <h2 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              Viability
            </h2>
            <span className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              Rough-cut capacity
            </span>
          </div>
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
            Confirm the candidate set is realistic in WU over the horizon — earlier confirmation,
            not detailed execution planning.
          </p>
          {exec ? (
            <div
              className={`text-lg font-bold ${
                exec.overallFeasibility === 'Fully Achievable'
                  ? 'text-[var(--green)] dark:text-[var(--text-ter)]'
                  : exec.overallFeasibility === 'Partially Achievable'
                    ? 'text-[var(--mustard)] dark:text-[var(--mustard)]'
                    : exec.overallFeasibility === 'Not Achievable'
                      ? 'text-[var(--coral)] dark:text-[var(--coral)]'
                      : 'text-[var(--text-ter)] dark:text-[var(--text-sec)]'
              }`}
            >
              {exec.overallFeasibility}
            </div>
          ) : (
            <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)]">No viability run yet.</p>
          )}
          <div className="flex flex-wrap gap-2 mt-auto pt-1">
            <Link
              to="/planning/engine/above-below"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--rule)] dark:border-[var(--rule)] text-xs font-medium hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
            >
              Above / Below
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <Link
              to="/planning/engine/capacity"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--rule)] dark:border-[var(--rule)] text-xs font-medium hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
            >
              Resource budget
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        {timePhasedEnabled && (
          <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--slate)] dark:border-[var(--slate)]/40 p-5 flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Rocket className="h-5 w-5 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
              <h2 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                Launch planning
              </h2>
              <span className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                Time-phased execution
              </span>
            </div>
            <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
              Detailed planning most people mean by “planning” — place launches on shared teams,
              with supply hours and funding as peers.
            </p>
            <div className="flex flex-wrap gap-2 mt-auto pt-1">
              <Link
                to="/planning/launch-calendar"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium"
              >
                Launch Calendar
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <Link
                to="/planning/engine/time-phased"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--rule)] dark:border-[var(--rule)] text-xs font-medium hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
              >
                Supply &amp; profiles
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <Link
                to="/planning/engine/funding"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--rule)] dark:border-[var(--rule)] text-xs font-medium hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
              >
                Funding
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Promote to plan cycle CTA — shown after a successful run */}
      {planOutput && (
        <div className="border border-[var(--green)] dark:border-[var(--green)]/60 bg-[var(--green-dim)] dark:bg-[var(--green)]/20 p-5 flex items-start gap-4">
          <CheckCircle2 className="h-5 w-5 text-[var(--green)] dark:text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">
              Viability run complete
            </p>
            <p className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] mt-0.5">
              Review Above / Below, then review the summary and propose a plan version when ready to
              authorize — or continue into Launch planning for calendar placement.
            </p>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <Link
              to="/planning/engine/above-below"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--green)] dark:border-[var(--green)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] text-[var(--green)] dark:text-[var(--text-ter)] text-xs font-medium hover:bg-[var(--green-dim)] dark:hover:bg-[var(--green)]/20 transition-colors"
            >
              Review above / below line
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <Link
              to="/planning/engine/summary"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--green)] hover:bg-[var(--green)] text-white text-xs font-medium transition-colors"
            >
              {REVIEW_AND_COMMIT_LABEL}
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      )}

      {planOutput && (
        <PlanningEngineSetupChecklist
          activeScenario={activeScenario}
          projectId={projectId}
          planOutput={planOutput}
          executiveSummary={exec}
          onCreateScenario={() => setShowCreateScenario(true)}
          timePhasedEnabled={timePhasedEnabled}
        />
      )}

      {showCreateScenario && (
        <CreateScenarioModal
          onClose={() => setShowCreateScenario(false)}
          onCreated={() => {
            setShowCreateScenario(false);
            void refreshScenarios();
          }}
        />
      )}

      {/* Quick nav — grouped by capability */}
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] p-6 space-y-5">
        <div>
          <h2 className="text-sm font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider mb-1">
            Viability inputs &amp; results
          </h2>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-3">
            Rough-cut capacity path — Resource Budget is WU, not Launch Calendar supply.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { label: 'Scenarios', description: 'Create and activate scenarios', href: '/planning/engine/scenarios' },
              { label: 'Priorities', description: 'Items with WU estimates', href: '/planning/engine/priorities' },
              { label: 'Work Units', description: 'Optional decomposition', href: '/planning/engine/work-units' },
              { label: 'Dependencies', description: 'Blocking relationships', href: '/planning/engine/dependencies' },
              { label: 'Resource Budget', description: 'WU capacity by resource type', href: '/planning/engine/capacity' },
              { label: 'Above / Below', description: 'Viability communication view', href: '/planning/engine/above-below' },
              { label: 'Compare', description: 'Side-by-side candidates', href: '/planning/engine/compare' },
              { label: 'Engine Output', description: 'Full viability readout', href: '/planning/engine/engine-output' },
              // Run History is the only surface that answers "what did this
              // scenario × scope produce BEFORE the last run" — planning_runs is
              // overwritten every Sync & Run, planning_run_history is not. It was
              // routed but linked from nowhere, so the saved results were
              // unreachable in the product (defect: 2026-09-02 demo).
              { label: 'Run History', description: 'Every past run, by scenario and scope', href: '/planning/engine/run-history' },
            ].map((tab) => (
              <Link
                key={tab.href}
                to={tab.href}
                className="flex flex-col gap-1 px-4 py-3 border border-[var(--rule)] dark:border-[var(--rule)] hover:border-[var(--slate)] dark:hover:border-[var(--slate)] hover:bg-[var(--slate-dim)]/50 dark:hover:bg-[var(--slate)]/10 transition-colors"
              >
                <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">{tab.label}</span>
                <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">{tab.description}</span>
              </Link>
            ))}
          </div>
        </div>

        {timePhasedEnabled && (
          <div>
            <h2 className="text-sm font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider mb-1">
              Launch planning inputs &amp; view
            </h2>
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-3">
              Time-phased execution — shared teams, supply calendar, funding, Launch Calendar.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { label: 'Participation', description: 'Shared-team requirements', href: '/planning/engine/participation' },
                { label: 'Time-Phased', description: 'Supply calendar and workload templates', href: '/planning/engine/time-phased' },
                { label: 'Funding & Costs', description: 'Envelope and launch cost plans', href: '/planning/engine/funding' },
                { label: 'Launch Calendar', description: 'Buildable vs launchable', href: '/planning/launch-calendar' },
              ].map((tab) => (
                <Link
                  key={tab.href}
                  to={tab.href}
                  className="flex flex-col gap-1 px-4 py-3 border border-[var(--slate)] dark:border-[var(--slate)]/40 hover:border-[var(--slate)] dark:hover:border-[var(--slate)] hover:bg-[var(--slate-dim)]/50 dark:hover:bg-[var(--slate)]/10 transition-colors"
                >
                  <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">{tab.label}</span>
                  <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">{tab.description}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
